import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  EventEmitter,
  Output
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { OrganisationService } from '../../../core/data/organisation.service';
import { IndustryService } from '../../../core/data/industry.service';
import { SnackbarQueueService } from '../../../core/utils/snackbar-queue/snackbar-queue.service';

import { Organisation } from '../../../core/models/organistion.model';
import { Industry } from '../../../core/models/industry.model';

@Component({
  selector: 'idl-organisation-edit',
  templateUrl: './organisation-edit.component.html',
  styleUrls: ['./organisation-edit.component.scss']
})
export class OrganisationEditComponent implements OnInit, OnDestroy {
  organisationForm = this._fb.group({
    name: ['', Validators.required],
    comments: [''],
    crmLink: [''],
    industries: ['']
  });

  private unsubscribe$ = new Subject<void>();

  @Input() addOrg: boolean;

  @Input() organisation: Organisation;
  industries$: Observable<Industry[]>;

  existingIndustries: Industry[] = [];
  newIndustries: Industry[] = [];

  @Output() saved: EventEmitter<any> = new EventEmitter();
  @Input() saveEvent: Observable<void>;

  submitted = false;

  constructor(
    private _organisationService: OrganisationService,
    private _fb: UntypedFormBuilder,
    private _industryService: IndustryService,
    private _snackBar: SnackbarQueueService
  ) {
    this.industries$ = this._industryService.list();
  }

  ngOnInit() {
    this.saveEvent
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.saveOrg());

    this.existingIndustries = Object.assign(
      [],
      this.organisation.industries.map(Industry.adapt)
    );
    this.newIndustries = this.organisation.industries.map(Industry.adapt);

    this.organisationForm.patchValue({
      name: this.organisation.name,
      comments: this.organisation.comments,
      crmLink: this.organisation.crmLink,
      industries: this.organisation.industries
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // Used to ensure multi select is properly pre populated
  compareWithFn(item1, item2) {
    return item1 && item2
      ? item1.industryId === item2.industryId
      : item1 === item2;
  }

  saveOrg() {
    this.submitted = true;

    this.organisation.name = this.organisationForm.value.name;
    this.organisation.comments = this.organisationForm.value.comments;
    this.organisation.crmLink = this.organisationForm.value.crmLink;
    this.organisation.industries = this.organisationForm.value.industries;
    this.newIndustries = this.organisationForm.value.industries;

    this._organisationService
      .edit(this.organisation)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        org => {
          const deleteInd = this.existingIndustries.filter(
            item =>
              this.newIndustries.findIndex(
                i => i.industryId === item.industryId
              ) < 0
          );
          const addInd = this.newIndustries.filter(
            item =>
              this.existingIndustries.findIndex(
                i => i.industryId === item.industryId
              ) < 0
          );

          deleteInd.forEach(industry => {
            this._organisationService
              .deleteIndustry(org.organisationId, industry.industryId)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe(delresponse => {
                const index = this.existingIndustries.findIndex(
                  o => o.industryId === industry.industryId
                );
                if (index >= 0) {
                  this.existingIndustries.splice(index, 1);
                }
                this._snackBar.show('Removed Industry:', industry.name, {
                  duration: 2000,
                  verticalPosition: 'top'
                });
              });
          });

          addInd.forEach(industry => {
            this._organisationService
              .addIndustry(org.organisationId, industry.industryId)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe(response => {
                this.existingIndustries = response;
                this._snackBar.show('Added Industry:', industry.name, {
                  duration: 2000,
                  verticalPosition: 'top'
                });
              });
          });

          this.submitted = false;

          if (this.addOrg) {
            this._snackBar.show(
              'New organisation successfully created.',
              'New Organisation',
              {
                duration: 2000,
                verticalPosition: 'top'
              }
            );
          } else {
            this._snackBar.show('Organisation successfully saved.', 'Saved', {
              duration: 2000,
              verticalPosition: 'top'
            });
          }

          this.saved.emit(org);
        },
        err => {
          this._snackBar.show('Error saving organisation.', 'Error', {
            duration: 5000,
            verticalPosition: 'top'
          });
        }
      );
  }
}
